import { ElementRef, Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';
import { ChartsUtils } from '@shared/components/charts/charts.utils';
import { select } from 'd3';
import Konva from 'konva';
import { Vector2d } from 'konva/lib/types';
import { BehaviorSubject, Subject } from 'rxjs';
import { MinimapScaleFactor } from '../models/sales-plan-constants';

@Injectable({
  providedIn: 'root',
})
export class SalesPlanBlockViewMinimapService {
  private readonly rendererFactory = inject(RendererFactory2);

  private initialScale: number;
  private currentScale: number;
  private topviewPlan: SVGSVGElement;
  private container: ElementRef;
  private renderer: Renderer2;

  public minimapStage: Konva.Stage;
  public mainStage: Konva.Stage;
  private minimapLayer: Konva.Layer;
  private actionLayer: Konva.Layer;
  private scaleRect: Konva.Rect;

  public scaleRectPosition$: Subject<Vector2d>;
  constructor() {
    const rendererFactory = this.rendererFactory;

    this.renderer = rendererFactory.createRenderer(null, null);
  }

  init(stage: Konva.Stage, container: ElementRef, topviewPlan: SVGSVGElement) {
    this.destroyMinimap();
    this.mainStage = stage;
    this.initialScale = this.mainStage.scaleX() * MinimapScaleFactor;
    this.topviewPlan = topviewPlan;
    this.container = container;
    this.prepareBlockviewMinimap();
    this.scaleRectPosition$ = new BehaviorSubject<Vector2d>(this.scaleRect.position());
    if (this.topviewPlan) {
      this.prepareTopviewMinimap();
    }
    this.toggleMinimapView(true);
  }

  private prepareBlockviewMinimap() {
    this.minimapStage = new Konva.Stage({
      container: this.container?.nativeElement,
      width: this.mainStage?.width() * MinimapScaleFactor,
      height: this.mainStage?.height() * MinimapScaleFactor,
      scaleX: this.initialScale,
      scaleY: this.initialScale,
      visible: false,
    });
    this.actionLayer = new Konva.Layer();
    this.minimapLayer = new Konva.Layer({ listening: false });
    this.minimapStage.add(this.minimapLayer);
    this.minimapStage.add(this.actionLayer);
    this.createScaleRect();
  }

  private prepareTopviewMinimap() {
    select(this.topviewPlan).classed('minimap', true);
    this.renderer.appendChild(this.container.nativeElement, this.topviewPlan);
  }

  public addMinimapElement(node: Konva.Group, x: number, y: number) {
    if (node) {
      this.minimapLayer.add(
        node.clone({
          width: node?.width(),
          height: node?.height(),
          x: x,
          y: y,
        }),
      );
    }
  }

  public cacheMinimapLayer() {
    this.minimapLayer.batchDraw();
  }

  public toggleMinimapView(isInitialScale: boolean) {
    if (this.topviewPlan) {
      if (isInitialScale) {
        this.topviewPlan.setAttribute('display', `block`);
        this.minimapStage.hide();
      } else {
        this.topviewPlan.setAttribute('display', `none`);
        this.minimapStage.show();
      }
    } else {
      if (!this.minimapStage.isVisible()) {
        this.minimapStage.show();
      }
    }
  }

  private createScaleRect() {
    this.scaleRect = new Konva.Rect({
      x: 0,
      y: 0,
      width: this.minimapStage.width() / this.initialScale,
      height: this.minimapStage.height() / this.initialScale,
      fill: ChartsUtils.getColorByPropertyName('--color-scale-rect-bgrd'),
      stroke: ChartsUtils.getColorByPropertyName('--color-scale-rect-border'),
      strokeWidth: 1,
      draggable: true,
    });
    this.actionLayer.add(this.scaleRect);
    this.scaleRect.on('dragend', () => {
      const { x, y } = this.scaleRect.position();
      this.scaleRectPosition$.next({ x: x * this.currentScale * -1, y: y * this.currentScale * -1 });
    });
  }

  public rescaleMinimap() {
    this.initialScale = this.mainStage.scaleX() * MinimapScaleFactor;
    this.toggleMinimapView(true);
    this.minimapStage.width(this.mainStage.width() * MinimapScaleFactor);
    this.minimapStage.height(this.mainStage.height() * MinimapScaleFactor);
    this.minimapStage.position({ x: 0, y: 0 });
    this.minimapStage.scale({ x: this.initialScale, y: this.initialScale });
  }

  public updateScaleRectOnZoom(scale: number, position: Vector2d) {
    if (this.mainStage) {
      this.currentScale = scale;
      this.scaleRect.setAttrs({
        x: (position?.x * -1) / scale,
        y: (position?.y * -1) / scale,
        width: this.minimapStage.width() / (scale * MinimapScaleFactor),
        height: this.minimapStage.height() / (scale * MinimapScaleFactor),
      });
    }
  }

  public destroyMinimap() {
    if (this.minimapStage) {
      this.minimapStage.destroy();
      if (this.topviewPlan) {
        this.renderer.removeChild(this.container?.nativeElement, this.topviewPlan);
      }
      this.scaleRectPosition$.complete();
    }
  }
}
